/**
 * Доска элементов.
 *
 * Колонки строятся по выбранному признаку, перетаскивание меняет значение
 * этого признака — то есть доска не отдельная сущность, а другой способ
 * редактировать те же поля через ту же операцию изменения элемента.
 *
 * Группировка по статусу возможна только когда в фильтре выбран один тип:
 * у пятнадцати типов свои наборы статусов, и объединение дало бы четыре
 * десятка колонок, в каждой из которых лежал бы один элемент.
 */

import {
  closestCorners,
  DndContext,
  DragOverlay,
  PointerSensor,
  useDroppable,
  useSensor,
  useSensors,
  type DragEndEvent,
  type DragStartEvent,
} from '@dnd-kit/core';
import { SortableContext, verticalListSortingStrategy } from '@dnd-kit/sortable';
import { useCallback, useMemo, useRef, useState } from 'react';
import { useWheelPan } from '@/lib/use-wheel';
import { toast } from 'sonner';
import { useLanded } from '@/lib/use-landed';
import type { ItemSummaryDto, ItemType, WorkstreamDto } from '@project-os/contracts';
import {
  getItemTypeDefinition,
  isValidStatusForType,
  PRIORITIES,
  PRIORITY_LABELS_RU,
  type ProjectMemberDto,
} from '@project-os/contracts';
import { ApiError } from '@/api/client';
import { useUpdateItem } from '@/api/items';
import { cn } from '@/lib/utils';
import { DraggableItemCard, ItemCardOverlay } from './ItemCard';

export type BoardGrouping = 'status' | 'workstream' | 'owner' | 'priority';

const NONE_COLUMN = '__none__';

interface Column {
  id: string;
  title: string;
  hint?: string;
}

export function ItemsBoard({
  projectId,
  items,
  grouping,
  singleType,
  workstreams,
  members,
  canEdit,
  onOpenItem,
}: {
  projectId: string;
  items: ItemSummaryDto[];
  grouping: BoardGrouping;
  /** Единственный тип в фильтре — только тогда колонки по статусу осмысленны. */
  singleType: ItemType | null;
  workstreams: WorkstreamDto[];
  members: ProjectMemberDto[];
  canEdit: boolean;
  onOpenItem: (itemId: string) => void;
}) {
  const update = useUpdateItem(projectId);
  const [dragging, setDragging] = useState<ItemSummaryDto | null>(null);

  const boardRef = useRef<HTMLDivElement>(null);
  useWheelPan(boardRef);

  const sensors = useSensors(
    // Перетаскивание начинается после небольшого смещения: иначе обычный
    // щелчок по карточке превращался бы в перетаскивание.
    useSensor(PointerSensor, { activationConstraint: { distance: 5 } }),
  );

  const columns = useMemo<Column[]>(() => {
    switch (grouping) {
      case 'status': {
        if (!singleType) return [];
        return getItemTypeDefinition(singleType).statuses.map((status) => ({
          id: status.value,
          title: status.labelRu,
        }));
      }
      case 'workstream':
        return [
          ...workstreams.map((workstream) => ({ id: workstream.id, title: workstream.name })),
          { id: NONE_COLUMN, title: 'Без направления' },
        ];
      case 'owner':
        return [
          ...members.map((member) => ({ id: member.user.id, title: member.user.name })),
          { id: NONE_COLUMN, title: 'Не назначен' },
        ];
      case 'priority':
        return [
          ...PRIORITIES.map((priority) => ({ id: priority, title: PRIORITY_LABELS_RU[priority] })),
          { id: NONE_COLUMN, title: 'Без приоритета' },
        ];
    }
  }, [grouping, singleType, workstreams, members]);

  const columnOf = useCallback(
    (item: ItemSummaryDto): string => {
      switch (grouping) {
        case 'status':
          return item.status;
        case 'workstream':
          return item.workstream?.id ?? NONE_COLUMN;
        case 'owner':
          return item.owner?.id ?? NONE_COLUMN;
        case 'priority':
          return item.priority ?? NONE_COLUMN;
      }
    },
    [grouping],
  );

  const grouped = useMemo(() => {
    const map = new Map<string, ItemSummaryDto[]>();
    for (const column of columns) map.set(column.id, []);
    for (const item of items) {
      const key = columnOf(item);
      const list = map.get(key);
      if (list) list.push(item);
      else map.set(key, [item]);
    }
    return map;
  }, [items, columns, columnOf]);

  const { landedId, markLanded } = useLanded();

  const handleDragEnd = async (event: DragEndEvent) => {
    setDragging(null);

    const item = event.active.data.current?.item as ItemSummaryDto | undefined;
    // Целью может оказаться и карточка, и колонка — интересует колонка.
    const overData = event.over?.data.current as { columnId?: string } | undefined;
    const target = overData?.columnId ?? (event.over?.id ? String(event.over.id) : null);
    if (!item || !target || columnOf(item) === target) return;

    const value = target === NONE_COLUMN ? null : target;

    // Правила типа проверяются до запроса: сервер откажет так же, но
    // объяснение лучше показать сразу и не терять перетаскивание.
    if (grouping === 'status' && value && !isValidStatusForType(item.type, value)) {
      toast.error(
        `Статус «${value}» недопустим для типа «${getItemTypeDefinition(item.type).labelRu}»`,
      );
      return;
    }
    if (grouping === 'priority' && value && !getItemTypeDefinition(item.type).supportsPriority) {
      toast.error(`Приоритет не применяется к типу «${getItemTypeDefinition(item.type).labelRu}»`);
      return;
    }

    try {
      await update.mutateAsync({
        itemId: item.id,
        ...(grouping === 'status' ? { status: value ?? undefined } : {}),
        ...(grouping === 'workstream' ? { workstreamId: value } : {}),
        ...(grouping === 'owner' ? { ownerId: value } : {}),
        ...(grouping === 'priority' ? { priority: value as ItemSummaryDto['priority'] } : {}),
      });
      markLanded(item.id);
    } catch (error) {
      toast.error(error instanceof ApiError ? error.message : 'Не удалось переместить элемент');
    }
  };

  if (grouping === 'status' && !singleType) {
    return (
      <div className="px-6 py-12 text-center">
        <p className="text-sm">Колонки по статусу доступны для одного типа</p>
        <p className="mx-auto mt-1.5 max-w-md text-xs leading-relaxed text-muted-foreground">
          У каждого типа свой набор статусов: «Задача» проходит путь от «Не начата» до «Выполнена»,
          а «Факт» — от «Не проверен» до «Подтверждён». Выберите один тип в фильтре либо
          сгруппируйте доску по направлению или ответственному.
        </p>
      </div>
    );
  }

  return (
    <DndContext
      collisionDetection={closestCorners}
      sensors={sensors}
      onDragStart={(event: DragStartEvent) =>
        setDragging((event.active.data.current?.item as ItemSummaryDto) ?? null)
      }
      onDragEnd={(event) => void handleDragEnd(event)}
      onDragCancel={() => setDragging(null)}
    >
      {/* Колонки уходят за правый край, а двигать их вертикально нечего:
          обычное колесо мыши здесь означает «вбок». Колонка, которой ещё есть
          куда прокрутиться, жест не отдаёт — читать длинный список важнее. */}
      <div ref={boardRef} data-board className="flex h-full gap-3 overflow-x-auto px-6 pt-1 pb-4">
        {columns.map((column) => (
          <BoardColumn
            key={column.id}
            column={column}
            items={grouped.get(column.id) ?? []}
            canEdit={canEdit}
            onOpenItem={onOpenItem}
            landedId={landedId}
          />
        ))}
      </div>

      <DragOverlay dropAnimation={null}>
        {dragging ? <ItemCardOverlay item={dragging} /> : null}
      </DragOverlay>
    </DndContext>
  );
}

function BoardColumn({
  column,
  items,
  canEdit,
  onOpenItem,
  landedId,
}: {
  column: Column;
  items: ItemSummaryDto[];
  canEdit: boolean;
  onOpenItem: (itemId: string) => void;
  landedId: string | null;
}) {
  const { setNodeRef, isOver } = useDroppable({
    id: column.id,
    data: { columnId: column.id },
    disabled: !canEdit,
  });

  return (
    <section
      ref={setNodeRef}
      className={cn(
        'drop-zone flex w-72 shrink-0 flex-col rounded-xl',
        isOver ? 'bg-primary-soft ring-1 ring-primary/50' : 'bg-muted/40',
      )}
    >
      <header className="flex items-center gap-2 px-3 py-2.5">
        <h3 className="truncate text-xs font-semibold">{column.title}</h3>
        <span className="text-2xs text-muted-foreground tabular-nums">{items.length}</span>
      </header>

      <div data-column-body className="flex-1 space-y-2 overflow-y-auto px-2 pb-2">
        <SortableContext
          items={items.map((item) => item.id)}
          strategy={verticalListSortingStrategy}
        >
          {items.map((item) => (
            <DraggableItemCard
              key={item.id}
              item={item}
              columnId={column.id}
              onOpen={onOpenItem}
              landed={item.id === landedId}
            />
          ))}
        </SortableContext>

        {items.length === 0 ? (
          <p className="px-1 py-6 text-center text-xs text-muted-foreground/60">
            {canEdit ? 'Перетащите элемент сюда' : 'Пусто'}
          </p>
        ) : null}
      </div>
    </section>
  );
}
